import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Tabs,
  Tab,
  Alert,
  CircularProgress
} from '@mui/material';
import { Settings as SettingsIcon, ListAlt as ListIcon, Category, AccountBalance } from '@mui/icons-material';
import MatchingConfigurationPanel from '../components/MatchingConfigurationPanel';
import PlannedExpenseMatchesPanel from '../components/PlannedExpenseMatchesPanel';
import CategoriesPage from './CategoriesPage';
import BanksPage from './BanksPage';
import { matchingConfigurationAPI, MatchingConfiguration } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [config, setConfig] = useState<MatchingConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mock data for matches - replace with real API call
  const [matches, setMatches] = useState<any[]>([]);

  useEffect(() => {
    fetchConfiguration();
    fetchMatches();
  }, []);

  const fetchConfiguration = async () => {
    try {
      setLoading(true);
      const response = await matchingConfigurationAPI.get();
      setConfig(response.data);
    } catch (err: any) {
      setError('Failed to load configuration: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async () => {
    // Mock data for now - replace with real API call
    setMatches([
      {
        transaction: {
          id: 1,
          amount: 20421.83,
          description: 'AXIS MAX LIFE Insurance Payment',
          date: '2025-01-02',
          category_name: 'Healthcare',
          bank_name: 'AXIS Bank'
        },
        plannedExpense: {
          id: 1,
          title: 'AXIS MAX LIFE',
          category_name: 'Healthcare',
          expense_type: 'insurance',
          monthly_amount: 20421.83,
          next_due_date: '2025-01-01',
          frequency: 'monthly',
          priority: 'high',
          match_score: 95
        },
        autoMatched: true,
        status: 'pending'
      }
    ]);
  };

  const handleConfigSave = (newConfig: MatchingConfiguration) => {
    setConfig(newConfig);
    // You could show a success message here
  };

  const handleConfigReset = () => {
    fetchConfiguration();
  };

  const handleConfirmMatch = (matchId: number) => {
    // Update match status to confirmed
    setMatches(prev => prev.map(match => 
      match.transaction.id === matchId 
        ? { ...match, status: 'confirmed' }
        : match
    ));
  };

  const handleRejectMatch = (matchId: number) => {
    // Update match status to rejected
    setMatches(prev => prev.map(match => 
      match.transaction.id === matchId 
        ? { ...match, status: 'rejected' }
        : match
    ));
  };

  const handleModifyMatch = (matchId: number, plannedExpenseId: number) => {
    // Handle match modification
    console.log('Modifying match:', matchId, 'to planned expense:', plannedExpenseId);
  };

  const handleRefreshMatches = () => {
    fetchMatches();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Typography variant="h6">Settings</Typography>
        <Typography>Failed to load settings. Please try refreshing the page.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <SettingsIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4">Settings</Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          aria-label="settings tabs"
        >
          <Tab 
            icon={<SettingsIcon />} 
            label="Matching Configuration" 
            iconPosition="start"
          />
          <Tab 
            icon={<ListIcon />} 
            label="Planned Expense Matches" 
            iconPosition="start"
          />
          <Tab 
            icon={<Category />} 
            label="Categories" 
            iconPosition="start"
          />
          <Tab 
            icon={<AccountBalance />} 
            label="Banks" 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {config && (
          <MatchingConfigurationPanel
            config={config}
            onSave={handleConfigSave}
            onReset={handleConfigReset}
          />
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <PlannedExpenseMatchesPanel
          matches={matches}
          onConfirmMatch={handleConfirmMatch}
          onRejectMatch={handleRejectMatch}
          onModifyMatch={handleModifyMatch}
          onRefresh={handleRefreshMatches}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <CategoriesPage />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <BanksPage />
      </TabPanel>
    </Container>
  );
};

export default SettingsPage;


